import { RenderTargetTexture, WebGLHardwareTexture } from "babylonjs";

export default class WebGLBlitter {
  private fsProgram: undefined | WebGLProgram;
  private vertBuffer: undefined | WebGLBuffer;
  private textureLocation: WebGLUniformLocation | null = null;
  private positionLocation: number | null = null;

  private gl?: WebGL2RenderingContext;

  private createProgram() {
    const gl = this.gl;
    if (!gl) {
      console.error("WebGL context is not initialized.");
      return;
    }

    // Use a simple shader to draw the texture onto a fullscreen quad
    const vertexShaderSource = `
      attribute vec2 position;
      varying vec2 uv;
      void main() {
        uv = (position + 1.0) * 0.5; // Convert from clip space to UV coords
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision mediump float;
      varying vec2 uv;
      uniform sampler2D renderTexture;
      void main() {
        vec4 renderColor = texture2D(renderTexture, uv);
        gl_FragColor = renderColor;
      }
    `;

    // Compile shaders
    const compileShader = (type: number, source: string) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader!, source);
      gl.compileShader(shader!);
      if (!gl.getShaderParameter(shader!, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader!));
        return null;
      }
      return shader;
    };

    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = compileShader(
      gl.FRAGMENT_SHADER,
      fragmentShaderSource
    );

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader!);
    gl.attachShader(program, fragmentShader!);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }
    this.fsProgram = program;
  }

  private createVertBuffer() {
    const gl = this.gl;
    if (!gl) {
      console.error("WebGL context is not initialized.");
      return;
    }

    const vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    const vertices = new Float32Array([
      -1,
      -1,
      1,
      -1,
      -1,
      1, // First triangle
      -1,
      1,
      1,
      -1,
      1,
      1, // Second triangle
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    this.vertBuffer = vertexBuffer;
  }

  public cleanup() {
    const gl = this.gl;
    if (!gl) {
      console.error("WebGL context is not initialized.");
      return;
    }

    // GL Cleanup
    if (this.fsProgram !== undefined) {
      this.fsProgram = undefined;
      gl.deleteProgram(this.fsProgram!);
    }
    if (this.vertBuffer !== undefined) {
      this.vertBuffer = undefined;
      gl.deleteBuffer(this.vertBuffer!);
    }

    this.textureLocation = null;
    this.positionLocation = null;
  }

  public init(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.cleanup();
    this.createProgram();
    this.createVertBuffer();
  }

  public blitRTTToCanvas(
    renderTarget: RenderTargetTexture,
    width: number,
    height: number
  ) {
    const gl = this.gl;
    if (!gl) {
      console.error("WebGL context is not initialized.");
      return;
    }

    const renderTexture = renderTarget.getInternalTexture();

    if (
      !renderTexture ||
      !renderTexture._hardwareTexture ||
      !renderTexture._hardwareTexture.underlyingResource
    ) {
      console.error("RenderTargetTexture is missing its WebGL texture.");
      return;
    }

    if (!this.fsProgram || !this.vertBuffer) {
      this.init(gl);
      return;
    }

    gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    gl.viewport(0, 0, width, height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    gl.clearColor(
      0,
      0,
      0,
      1
    );
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertBuffer);

    // Set up attributes
    if (this.positionLocation === null) {
      this.positionLocation = gl.getAttribLocation(this.fsProgram, "position");
    }
    gl.enableVertexAttribArray(this.positionLocation);
    gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);
    gl.useProgram(this.fsProgram);

    if (!this.textureLocation) {
      this.textureLocation = gl.getUniformLocation(
        this.fsProgram,
        "renderTexture"
      );
    }

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(
      gl.TEXTURE_2D,
      (renderTexture._hardwareTexture as WebGLHardwareTexture)
        .underlyingResource
    );
    gl.uniform1i(this.textureLocation, 0);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.finish();
  }
}
